import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { EntryFormDialog } from '@/components/common/EntryFormDialog';
import { createAdjustor, updateAdjustor } from '../api/insurance';
import type { InsuranceAdjustor, InsuranceAdjustorFormData } from '../types';

interface AdjustorFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number;
  companyName: string;
  adjustor?: InsuranceAdjustor | null;
  onSuccess: () => void;
}

const initialFormData: InsuranceAdjustorFormData = {
  firstName: '',
  lastName: '',
  officePhone: '',
  cellPhone: '',
  email: '',
  isActive: true,
};

export function AdjustorForm({
  open,
  onOpenChange,
  companyId,
  companyName,
  adjustor,
  onSuccess,
}: AdjustorFormProps) {
  const [formData, setFormData] = useState<InsuranceAdjustorFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isEditMode = !!adjustor?.id;

  useEffect(() => {
    if (adjustor) {
      setFormData({
        firstName: adjustor.firstName || '',
        lastName: adjustor.lastName || '',
        officePhone: adjustor.officePhone || '',
        cellPhone: adjustor.cellPhone || '',
        email: adjustor.email || '',
        isActive: adjustor.isActive,
      });
    } else {
      setFormData(initialFormData);
    }
    setError('');
  }, [adjustor, open]);

  const handleChange = (field: keyof InsuranceAdjustorFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation - require at least first or last name
    if (!formData.firstName?.trim() && !formData.lastName?.trim()) {
      setError('At least first name or last name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && adjustor) {
        await updateAdjustor(adjustor.id, formData);
      } else {
        await createAdjustor(companyId, formData);
      }
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setError(errorObj.response?.data?.message || 'Failed to save adjustor');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EntryFormDialog
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={isEditMode ? 'Edit Adjustor' : 'Add Adjustor'}
      error={error}
      isSubmitting={isSubmitting}
      submitLabel="Add Adjustor"
      editLabel="Save Changes"
      isEditing={isEditMode}
      onSubmit={handleSubmit}
      maxWidth="max-w-md"
    >
      <div className="space-y-2">
        <Label>Company</Label>
        <div className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
          {companyName}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            value={formData.firstName || ''}
            onChange={(e) => handleChange('firstName', e.target.value)}
            placeholder="First name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            value={formData.lastName || ''}
            onChange={(e) => handleChange('lastName', e.target.value)}
            placeholder="Last name"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="officePhone">Office Phone</Label>
          <Input
            id="officePhone"
            type="tel"
            value={formData.officePhone || ''}
            onChange={(e) => handleChange('officePhone', e.target.value)}
            placeholder="(555) 123-4567"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="cellPhone">Cell Phone</Label>
          <Input
            id="cellPhone"
            type="tel"
            value={formData.cellPhone || ''}
            onChange={(e) => handleChange('cellPhone', e.target.value)}
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => handleChange('email', e.target.value)}
          placeholder="email@company.com"
        />
      </div>

      {isEditMode && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
          />
          <Label htmlFor="isActive" className="text-sm font-normal">
            Active
          </Label>
        </div>
      )}
    </EntryFormDialog>
  );
}
